import useBranches from "@/hooks/useBranches";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";
import { Buildings, Lock } from "@phosphor-icons/react";

/**
 * Branch picker for create/edit forms.
 *
 * - Admin: free choice across all active branches.
 * - Staff: hard-locked to their assigned branch. We display a read-only badge
 *   (matching the BranchFilter pattern) and sync the form value to their
 *   branch on mount so submission carries the correct id even if the form
 *   was opened with a blank slate.
 * - Staff without a branch: shown an inline error so they know to contact admin.
 *
 * Backend `resolve_create_branch` is the authoritative gate — this component
 * just keeps the UI consistent with what will actually be persisted.
 */
export default function BranchSelect({ value, onChange, testId = "branch-select", required = false }) {
  const { user } = useAuth();
  const { branches } = useBranches({ activeOnly: true });
  const isStaff = user?.role !== "admin";

  // Staff: force-sync the form value to their branch (overrides whatever was
  // there). Admin: only default a blank value.
  useEffect(() => {
    if (!user) return;
    if (isStaff) {
      if (user.branch_id && value !== user.branch_id) onChange(user.branch_id);
      return;
    }
    if (!value && user.branch_id && branches.some((b) => b.id === user.branch_id)) {
      onChange(user.branch_id);
    }
  }, [branches, user, value, onChange, isStaff]);

  if (isStaff) {
    if (!user?.branch_id) {
      return (
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
            Branch
          </label>
          <div
            data-testid={`${testId}-no-branch`}
            className="px-3 py-2 border border-destructive bg-destructive/5 text-sm text-destructive font-mono"
          >
            You are not assigned to a branch. Contact your admin.
          </div>
        </div>
      );
    }
    // Show the branch even before the branches list arrives so the UI doesn't
    // flicker into a misleading "no branch" state on first render.
    const own = branches.find((b) => b.id === user.branch_id);
    return (
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
          Branch
        </label>
        <div
          data-testid={`${testId}-locked`}
          className="inline-flex items-center gap-2 px-3 py-2 border border-canvas-line bg-canvas-panel text-sm text-ink"
        >
          <Buildings size={12} weight="bold" />
          <span>{own?.name || "Your branch"}</span>
          <Lock
            size={11}
            weight="bold"
            className="text-ink-subtle ml-1"
            aria-label="Locked to your assigned branch"
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        Branch
      </label>
      <select
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
      >
        <option value="" disabled>
          Select a branch…
        </option>
        {branches.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
    </div>
  );
}
